import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';

const FADE_AFTER_MS = 2500;

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
};

/**
 * Direction B's one full-screen control: the stage goes to browser full screen, or fills the page where the browser
 * can't (iPhone Safari). F toggles and the controls fade while the video plays. The player is a same-origin iframe that
 * keeps keys and mouse movement once clicked or hovered, so the listeners go on its window as well as this one.
 */
export function useStudioFullscreen(stageRef: RefObject<HTMLElement | null>, { enabled, playing }: { enabled: boolean; playing: boolean }) {
  const [native, setNative] = useState(false);
  const [filling, setFilling] = useState(false);
  const [controlsHidden, setControlsHidden] = useState(false);
  const fadeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);
  const on = native || filling;

  const toggle = useCallback(() => {
    if (document.fullscreenElement) {
      void document.exitFullscreen();
    } else if (filling) {
      setFilling(false);
    } else if (document.fullscreenEnabled && stageRef.current?.requestFullscreen) {
      stageRef.current.requestFullscreen().catch(() => setFilling(true));
    } else {
      setFilling(true);
    }
  }, [filling, stageRef]);

  const showControls = useCallback(() => {
    setControlsHidden(false);
    clearTimeout(fadeTimer.current);
    if (on && playing && !held.current) fadeTimer.current = setTimeout(() => setControlsHidden(true), FADE_AFTER_MS);
  }, [on, playing]);

  useEffect(() => {
    if (!enabled) return;
    const onChange = () => setNative(!!stageRef.current && document.fullscreenElement === stageRef.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, [enabled, stageRef]);

  useEffect(() => {
    showControls();
    return () => clearTimeout(fadeTimer.current);
  }, [showControls]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!enabled || !stage) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFilling(false);
      } else if (e.key === 'Tab') {
        held.current = true;
        showControls();
      } else if ((e.key === 'f' || e.key === 'F') && !e.metaKey && !e.ctrlKey && !e.altKey && !isTyping(e.target)) {
        e.preventDefault();
        toggle();
      }
    };
    // Where the mouse is, from its own moves: a control that full screen removes never reports the mouse leaving it.
    const onPointerMove = (e: PointerEvent) => {
      if (e.pointerType !== 'mouse') return;
      held.current = !!(e.target as Element).closest?.('[data-controls]');
      showControls();
    };
    const onTouch = () => {
      held.current = false;
      showControls();
    };
    const watched = new Set<Window>();
    const watch = (win: Window | null) => {
      if (!win || watched.has(win)) return;
      try {
        win.addEventListener('keydown', onKey);
        win.addEventListener('pointermove', onPointerMove);
        win.addEventListener('touchstart', onTouch, { passive: true });
        watched.add(win);
      } catch {
        // A sandboxed frame keeps its own events.
      }
    };
    const onLoad = (e: Event) => {
      if (e.target instanceof HTMLIFrameElement) watch(e.target.contentWindow);
    };
    watch(window);
    stage.querySelectorAll('iframe').forEach((frame) => watch(frame.contentWindow));
    stage.addEventListener('load', onLoad, true);
    return () => {
      stage.removeEventListener('load', onLoad, true);
      watched.forEach((win) => {
        try {
          win.removeEventListener('keydown', onKey);
          win.removeEventListener('pointermove', onPointerMove);
          win.removeEventListener('touchstart', onTouch);
        } catch {
          // The frame has gone.
        }
      });
    };
  }, [enabled, stageRef, toggle, showControls]);

  return { on, filling, toggle, controlsHidden: on && controlsHidden };
}
